import React, { useState } from "react";
import {
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@material-ui/core";
import { View } from "react-native-web";
import { PrimaryText } from "components/Common/PrimaryText";
import Table from "components/Common/TableComponent/Table/Table";
import DashboardItem from "screens/Dashboard/DashboardItem/DashboardItem";
import { useSubscriptionStats } from "hooks/useSubscriptionStats";

const PERIOD_WEEKLY = "weekly";
const PERIOD_MONTHLY = "monthly";

const formatCount = (value) => (typeof value === "number" ? String(value) : "N/A");

const rowsFor = (stats, period) => {
	const source = period === PERIOD_MONTHLY ? stats?.months : stats?.weeks;
	if (!Array.isArray(source)) {
		return [];
	}
	return [...source].sort((left, right) => {
		if (left.start === right.start) {
			return 0;
		}
		return left.start < right.start ? 1 : -1;
	});
};

const latestDay = (days) => {
	if (!Array.isArray(days) || days.length === 0) {
		return null;
	}
	return days.reduce((latest, day) => (!latest || day.date > latest.date ? day : latest), null);
};

const currentBucket = (buckets, isoDate) => {
	if (!Array.isArray(buckets) || buckets.length === 0) {
		return null;
	}
	return buckets.find((bucket) => bucket.start <= isoDate && isoDate <= bucket.end) ?? null;
};

const SubscriptionStatCard = ({ label, value }) => (
	<DashboardItem>
		<PrimaryText fontSize={20} fontWeight={700} style={{ marginVertical: 12 }}>
			{label}
		</PrimaryText>
		<PrimaryText fontSize={36} fontWeight={800}>
			{value}
		</PrimaryText>
	</DashboardItem>
);

const StoreSubscriptionStats = ({ label, stats }) => {
	const [period, setPeriod] = useState(PERIOD_WEEKLY);

	const activeDay = latestDay(stats?.days);
	const week = currentBucket(stats?.weeks, activeDay?.date);
	const month = currentBucket(stats?.months, activeDay?.date);
	const rows = rowsFor(stats, period);

	return (
		<View style={{ width: "100%", marginTop: 24 }}>
			<PrimaryText fontSize={20} fontWeight={700} style={{ marginVertical: 12 }}>
				{`${label} Subscriptions`}
			</PrimaryText>
			<Grid container className="dashboardSection">
				<Grid item container xs={12} className="dashboardRow" style={{ flexWrap: "wrap" }}>
					<SubscriptionStatCard label={`${label} Active Subscriptions`} value={formatCount(activeDay?.active)} />
					<SubscriptionStatCard label={`${label} New This Week`} value={formatCount(week?.new)} />
					<SubscriptionStatCard label={`${label} New This Month`} value={formatCount(month?.new)} />
				</Grid>
			</Grid>
			<View style={{ marginTop: 16, marginBottom: 12, maxWidth: 240 }}>
				<FormControl variant="outlined" size="small" style={{ width: "100%" }}>
					<InputLabel>Period</InputLabel>
					<Select
						value={period}
						onChange={(event) => setPeriod(event.target.value)}
						label="Period"
					>
						<MenuItem value={PERIOD_WEEKLY}>Weekly</MenuItem>
						<MenuItem value={PERIOD_MONTHLY}>Monthly</MenuItem>
					</Select>
				</FormControl>
			</View>
			{rows.length === 0 ? (
				<PrimaryText fontSize={14} style={{ color: "#666" }}>
					{`No ${label} subscription stats for this period`}
				</PrimaryText>
			) : (
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Period</TableCell>
							<TableCell>New</TableCell>
							<TableCell>Active at end</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={`${period}-${row.start}`}>
								<TableCell>{`${row.start} to ${row.end}`}</TableCell>
								<TableCell>{formatCount(row.new)}</TableCell>
								<TableCell>{formatCount(row.active)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</View>
	);
};

export const SubscriptionStats = () => {
	const { stats, loading, error } = useSubscriptionStats();

	if (loading) {
		return (
			<View style={{ padding: 20, alignItems: "center" }}>
				<CircularProgress />
				<PrimaryText>Loading subscription stats...</PrimaryText>
			</View>
		);
	}

	if (error) {
		return (
			<View style={{ padding: 20 }}>
				<PrimaryText style={{ color: "#c62828" }}>Error loading subscription stats: {error}</PrimaryText>
			</View>
		);
	}

	return (
		<>
			<StoreSubscriptionStats label="iOS" stats={stats} />
			<StoreSubscriptionStats label="Android" stats={stats?.google} />
		</>
	);
};
